import {
  ActionIcon,
  Button,
  COLOR_PALETTE,
  Chip,
  Divider,
  Flex,
  Group,
  Icon,
  Menu,
  Popover,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  TextSearch,
  desktopFirstQuery,
} from '@inflearn/ds-react';
import { KoOnly, useLang } from '@inflearn/i18n-tools';
import { faBarsFilter, faSearch, faSort, faXmark } from '@inflearn/pro-regular-svg-icons';
import { faCircleCheck } from '@inflearn/pro-solid-svg-icons';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { isEqual, merge, pick } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

import { CourseType, MyVoucherStudyingType } from '../../../../../resources/@types/courseSchema';
import useQueryHandleWithUserId from '../../../../hooks/urlQueryString/useQueryHandleWithUserId';
import type { StringifyObject } from '../../../../utils/typeUtil';
import { forLegacyParameter } from './forLegacyParameter';
import type { VoucherMyQueryParameter } from './queries/useClientApiVoucherMyQuery';
import { CoursesSort } from './sort';

export function MyCoursesPageFilter({
  filterState,
  changeFilter,
}: {
  filterState: StringifyObject<VoucherMyQueryParameter>;
  changeFilter: (value: StringifyObject<VoucherMyQueryParameter>) => void;
}) {
  const { USER_ID_KEY } = useQueryHandleWithUserId();

  const { isKo } = useLang();
  const [isKeywordOpen, keywordOpenHandler] = useDisclosure();

  useEffect(() => {
    const changedFilter = forLegacyParameter(filterState);

    if (isEqual(changedFilter, filterState)) {
      // ant-man-account에서 온게 아님
      return;
    }
    // ant-man-account에서 온 경우
    changeFilter(changedFilter);
  }, []);

  const isOfflineCourseType = filterState.courseType === CourseType.OFF;
  const isOnlineCourseType = filterState.courseType === CourseType.ON;
  const isChallengeCourseType = filterState.courseType === CourseType.CHALLENGE;
  const isMobile = useMediaQuery(desktopFirstQuery.mobile);

  const updateFilter = (key: keyof typeof filterState) => (value: string) => {
    const filterObject = merge(filterState, { pageNumber: '1', [key]: value });

    if (key === 'courseType') {
      const isOfflineType = value === CourseType.OFF;
      changeFilter(
        merge(
          pick(filterObject, [
            'courseType',
            'pageNumber',
            'isArchived',
            'searchKeyword',
            USER_ID_KEY,
          ]),
          {
            sort: isOfflineType ? CoursesSort.RECENT_APPLY : CoursesSort.RECENT_STUDYING,
          },
        ),
      );

      return;
    }

    changeFilter(filterObject);
  };

  return (
    <Stack spacing="md" mt={24}>
      <Flex
        justify="space-between"
        // gap={gapSize}
        wrap="nowrap"
        css={{
          ...(!isKo && {
            order: 2,
            justifyContent: 'flex-end',
          }),
        }}
      >
        {isMobile && isKeywordOpen ? null : (
          <KoOnly>
            <ScrollArea
              type="never"
              styles={{
                viewport: {
                  // @ts-ignore
                  overflowY: 'hidden !important',
                  // @ts-ignore
                  overflowX: 'scroll !important',
                },
              }}
            >
              <Group spacing={8} noWrap>
                {isOnlineCourseType && (
                  <CourseFilter
                    studyingValue={filterState.studying}
                    isReviewableValue={filterState.isReviewable}
                    isExcludeExpiredValue={filterState.isExcludeExpired}
                    updateStudyingFilter={updateFilter('studying')}
                    updateReviewableFilter={updateFilter('isReviewable')}
                    updateExcludeExpiredFilter={updateFilter('isExcludeExpired')}
                  />
                )}
                {isChallengeCourseType && (
                  <ChallengeFilter
                    studyingValue={filterState.studying}
                    isReviewableValue={filterState.isReviewable}
                    updateStudyingFilter={updateFilter('studying')}
                    updateReviewableFilter={updateFilter('isReviewable')}
                  />
                )}
                <CourseTypeFilter
                  value={filterState.courseType}
                  updateFilter={updateFilter('courseType')}
                />
              </Group>
            </ScrollArea>
          </KoOnly>
        )}
        <Group
          spacing={8}
          noWrap
          pl={isKeywordOpen ? 0 : 16}
          css={{ flex: isMobile ? 1 : undefined, justifyContent: 'flex-end' }}
        >
          <KeywordFilter
            value={filterState.searchKeyword}
            updateFilter={updateFilter('searchKeyword')}
            isOpen={isKeywordOpen}
            toggleOpen={keywordOpenHandler.toggle}
          />
          <SortMenu
            value={filterState.sort}
            updateFilter={updateFilter('sort')}
            isOfflineFilter={isOfflineCourseType}
          />
        </Group>
      </Flex>
    </Stack>
  );
}

function isTrueParameter(value: string | undefined) {
  return value === 'true';
}

/**
 * 온/오프라인 필터
 */
function CourseTypeFilter({
  value,
  updateFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
}) {
  const { t } = useTranslation();

  const options = CourseTypeFilter.option.map((option) => ({
    value: option.value,
    label: t(option.label),
  }));

  return (
    <>
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'filled' : 'default'}
          color={value === option.value ? 'dark' : 'infgreen'}
          size="md"
          radius="xl"
          px={16}
          onClick={() => updateFilter(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </>
  );
}

const STUDY_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  { value: MyVoucherStudyingType.STUDYING, label: '학습 중' },
  { value: MyVoucherStudyingType.COMPLETED, label: '완강' },
] as const;

const CHALLENGE_STUDY_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  { value: MyVoucherStudyingType.STUDYING, label: '학습 중' },
] as const;

/**
 * 챌린지 필터링 팝업
 */
function ChallengeFilter({
  studyingValue,
  isReviewableValue,
  updateStudyingFilter,
  updateReviewableFilter,
}: {
  studyingValue?: string;
  isReviewableValue?: string;
  updateStudyingFilter: (value: string) => void;
  updateReviewableFilter: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure(false);

  const segmentedOptions = CHALLENGE_STUDY_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.label),
  }));

  return (
    <Popover
      opened={opened}
      onClose={close}
      position="bottom-start"
      shadow="sm"
      radius="md"
      withinPortal={true}
    >
      <Popover.Target>
        <ActionIcon variant="default" size="n-md" radius="xl" onClick={toggle}>
          <Icon icon={faBarsFilter} size="md" color="gray.9" />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Stack spacing={8} pt={12} pb={16} px={16} w={260}>
          <Stack spacing={5}>
            <Text fz="sm" fw={500} lh={1.4} color="gray.6">
              {t('강의 필터링')}
            </Text>
            <SegmentedControl
              value={studyingValue || ''}
              onChange={updateStudyingFilter}
              data={segmentedOptions}
              fullWidth
            />
          </Stack>
          <Divider my={7.5} color="gray.2" />
          <Switch
            label={t('수강평 작성 가능한')}
            color="infgreen"
            size="sm"
            labelPosition="right"
            checked={isTrueParameter(isReviewableValue)}
            onChange={(e) => updateReviewableFilter(e.currentTarget.checked ? 'true' : '')}
            styles={{
              label: {
                color: COLOR_PALETTE.gray[6],
              },
            }}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
/**
 * 강의 필터링 팝업
 */
function CourseFilter({
  studyingValue,
  isReviewableValue,
  isExcludeExpiredValue,
  updateStudyingFilter,
  updateReviewableFilter,
  updateExcludeExpiredFilter,
}: {
  studyingValue?: string;
  isReviewableValue?: string;
  isExcludeExpiredValue?: string;
  updateStudyingFilter: (value: string) => void;
  updateReviewableFilter: (value: string) => void;
  updateExcludeExpiredFilter: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure(false);

  const segmentedOptions = STUDY_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.label),
  }));

  return (
    <Popover
      opened={opened}
      onClose={close}
      position="bottom-start"
      shadow="sm"
      radius="md"
      withinPortal={true}
    >
      <Popover.Target>
        <ActionIcon variant="default" size="n-md" radius="xl" onClick={toggle}>
          <Icon icon={faBarsFilter} size="md" color="gray.9" />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Stack spacing={8} w={280} pt={12} pb={24} px={16}>
          <Stack spacing={5}>
            <Text fz="sm" fw={500} lh={1.4} color="gray.6">
              {t('강의 필터링')}
            </Text>
            <SegmentedControl
              value={studyingValue || ''}
              onChange={updateStudyingFilter}
              data={segmentedOptions}
              fullWidth
            />
          </Stack>
          <Divider my={7.5} color="gray.2" />
          <Switch
            label={t('수강평 작성 가능한')}
            color="infgreen"
            size="sm"
            labelPosition="right"
            checked={isTrueParameter(isReviewableValue)}
            onChange={(e) => updateReviewableFilter(e.currentTarget.checked ? 'true' : '')}
            styles={{
              label: {
                color: COLOR_PALETTE.gray[6],
              },
            }}
          />
          <Switch
            label={t('만료된 강의 숨기기')}
            color="infgreen"
            size="sm"
            labelPosition="right"
            checked={isTrueParameter(isExcludeExpiredValue)}
            onChange={(e) => updateExcludeExpiredFilter(e.currentTarget.checked ? 'true' : 'false')}
            styles={{
              label: {
                color: COLOR_PALETTE.gray[6],
              },
            }}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export const COURSE_TYPE = {
  ON: '온라인 강의',
  CHALLENGE: '챌린지',
  OFF: '모임/부트캠프',
} as const;

CourseTypeFilter.option = Object.entries(COURSE_TYPE).map(([value, label]) => ({
  value,
  label: label,
}));

/**
 * 키워드 검색 (지공자, 강의명 등)
 */
function KeywordFilter({
  value = '',
  updateFilter,
  isOpen,
  toggleOpen,
}: {
  value?: string;
  updateFilter: (value: string) => void;
  isOpen: boolean;
  toggleOpen: () => void;
}) {
  const isMobile = useMediaQuery(desktopFirstQuery.mobile);
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState(value);

  useEffect(() => {
    setKeyword(value);
  }, [value]);

  const handleEnter = () => {
    updateFilter(keyword);
  };

  const handleClickSearchIcon = () => {
    updateFilter(keyword);
  };

  const handleClear = () => {
    setKeyword('');
    updateFilter('');
  };

  return (
    <Flex align="center" w={isOpen ? '100%' : undefined} gap={8}>
      {isOpen && (
        <TextSearch size="md" width={isMobile ? '100%' : 200} css={{ flexGrow: 1, height: 42 }}>
          <TextInput
            onKeyDown={(e) => e.key === 'Enter' && handleEnter()}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={t('강의명, 지식공유자')}
            value={keyword}
            size="md"
            styles={{
              input: {
                border: 'none',
              },
            }}
          />

          <TextSearch.ActionIcon onClick={handleClickSearchIcon} />
        </TextSearch>
      )}
      {!isOpen && (
        <ActionIcon
          variant="default"
          onClick={toggleOpen}
          size={'n-md'}
          aria-label={t('키워드 검색열기')}
        >
          <Icon icon={faSearch} size="sm" />
        </ActionIcon>
      )}

      {isOpen && (
        <ActionIcon
          variant="light"
          color="dark"
          size="n-md"
          radius="md"
          onClick={() => {
            toggleOpen();
            handleClear();
          }}
        >
          <Icon icon={faXmark} size="md" color="dark.7" />
        </ActionIcon>
      )}
    </Flex>
  );
}

/**
 * 완강, 학습중 필터
 */
function StudyFilter({
  value,
  updateFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      {[MyVoucherStudyingType.STUDYING, MyVoucherStudyingType.COMPLETED].map((studyEnum) => (
        <Chip
          key={studyEnum}
          onClick={() => updateFilter(studyEnum === value ? '' : studyEnum)}
          checked={StudyFilter.option[studyEnum].value === value}
          variant="outline"
          color="gray"
          size="xs"
          radius="xl"
        >
          <span>{t(StudyFilter.option[studyEnum].label)}</span>
        </Chip>
      ))}
    </>
  );
}

StudyFilter.option = {
  [MyVoucherStudyingType.STUDYING]: {
    value: MyVoucherStudyingType.STUDYING,
    label: '학습중',
  },
  [MyVoucherStudyingType.COMPLETED]: {
    value: MyVoucherStudyingType.COMPLETED,
    label: '완강.short',
  },
} as const;

export const MY_COURSES_SORTING = {
  RECENT_STUDYING: '최근 학습순',
  RECENT_APPLY: '최근 신청순',
  COURSE_TITLE: '제목순',
  EARLIEST_EXPIRATION: '만료일순',
} as const;

/**
 * 정렬 메뉴 (모바일)
 */
function SortMenu({
  value,
  updateFilter,
  isOfflineFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
  isOfflineFilter: boolean;
}) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      (isOfflineFilter
        ? SortOption.option.filter(
            (item) =>
              item.value !== CoursesSort.RECENT_STUDYING &&
              item.value !== CoursesSort.EARLIEST_EXPIRATION,
          )
        : SortOption.option
      ).map((option) => ({ value: option.value, label: t(option.label) })),
    [isOfflineFilter, t],
  );

  return (
    <Menu
      position="bottom-end"
      shadow="sm"
      radius="md"
      styles={{ dropdown: { width: '200px !important' } }}
    >
      <Menu.Target>
        <ActionIcon variant="default" color="infgreen" size="n-md" radius="md">
          <Icon icon={faSort} size="md" color="gray.9" />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{t('정렬')}</Menu.Label>
        {options.map((option) => (
          <Menu.Item
            key={option.value}
            onClick={() => updateFilter(option.value)}
            rightSection={
              value === option.value ? (
                <Icon icon={faCircleCheck} size="sm" color="infgreen.7" />
              ) : null
            }
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * 정렬
 * @constructor
 */
function SortOption({
  value,
  updateFilter,
  isOfflineFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
  isOfflineFilter: boolean;
}) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      (isOfflineFilter
        ? SortOption.option.filter(
            (item) =>
              item.value !== CoursesSort.RECENT_STUDYING &&
              item.value !== CoursesSort.EARLIEST_EXPIRATION,
          )
        : SortOption.option
      ).map((option) => ({ value: option.value, label: t(option.label) })),
    [isOfflineFilter],
  );

  return <Select w={180} size="sm" value={value} data={options} onChange={updateFilter} />;
}

SortOption.option = Object.entries(MY_COURSES_SORTING).map(([value, label]) => ({
  value,
  label,
}));
